import I18n from 'components/I18n'
import { BaseMoney, CounterMoney } from 'components/PairMoney'
import Tooltips from 'components/Tooltips'
import { parseSymbol } from 'data/Instrument/types'
import messages from 'lang'
import React from 'react'
import { FormattedDate, MessageDescriptor } from 'react-intl'
import { SideType } from 'scenes/MarketData/types'
import { InformationIcon } from 'scenes/Trade/Ticket/TradeInput'
import { serialiseDate } from 'services/api'

import { capitalize } from '../../../utils/strings'
import { DetailsWrapper, Label, Value } from './utils'

type Props = {
  amount: number
  estimatedTotal?: React.ReactNode
  fees?: React.ReactNode
  fillPrice?: number
  goodToDate?: Date
  limitPrice?: number
  liquidationPrice?: number
  liquidity?: boolean
  markPrice?: number
  minAmount?: number
  positionMargin?: number
  positionMarginCurrency?: string
  remaining?: number
  side?: SideType
  status?: string
  stopLoss?: number
  symbol: string
  takeProfit?: number
  tif?: MessageDescriptor
  time?: string
  triggerPrice?: number
  type: string
}

const OrderDetails = ({
  amount,
  estimatedTotal,
  fees,
  fillPrice,
  goodToDate,
  limitPrice,
  liquidationPrice,
  liquidity,
  markPrice,
  minAmount,
  positionMargin,
  positionMarginCurrency,
  remaining,
  side,
  status,
  stopLoss,
  symbol,
  takeProfit,
  tif,
  time,
  triggerPrice,
  type
}: Props) => {
  const { base: baseCurrency } = parseSymbol(symbol)
  return (
    <DetailsWrapper>
      {status && (
        <div>
          <Label>
            <I18n data={messages.status} />
          </Label>
          <Value>{capitalize(status)}</Value>
        </div>
      )}
      <div>
        <Label>
          <I18n data={messages.amount} />
          <Tooltips name='order_confirmation.amount' placement='top' wrap={false}>
            <InformationIcon />
          </Tooltips>
        </Label>
        <Value data-e2e='confirmationOrderQuantity'>
          {side === 'buy' && '~'}
          <BaseMoney amount={amount} pair={symbol} />
        </Value>
      </div>
      {remaining && (
        <div>
          <Label>
            <I18n data={messages.filled_remaining} />
          </Label>
          <Value data-e2e='confirmationOrderFilledRemainingQuantity'>
            <BaseMoney amount={amount - remaining} pair={symbol} />
            <>(</>
            <BaseMoney amount={remaining} pair={symbol} />
            <>)</>
          </Value>
        </div>
      )}
      {positionMargin && (
        <div>
          <Label>
            <I18n data={messages.collateral} />
          </Label>
          <Value>
            {positionMarginCurrency === baseCurrency ? (
              <BaseMoney amount={positionMargin} pair={symbol} />
            ) : (
              <CounterMoney amount={positionMargin} pair={symbol} />
            )}
          </Value>
        </div>
      )}
      {markPrice && (
        <div>
          <Label>
            <I18n data={messages.mark_price} />
            <Tooltips name='mark_price_tooltip' placement='top' wrap={false}>
              <InformationIcon />
            </Tooltips>
          </Label>
          <Value>
            <CounterMoney amount={markPrice} pair={symbol} />
          </Value>
        </div>
      )}
      {fillPrice ? (
        <div>
          <Label>
            <I18n data={messages.fill_price} />
          </Label>
          <Value>
            <CounterMoney amount={fillPrice} pair={symbol} />
          </Value>
        </div>
      ) : null}
      {!!stopLoss && (
        <div>
          <Label>
            <I18n data={messages.stop_loss} />
          </Label>
          <Value>
            <CounterMoney amount={stopLoss} pair={symbol} />
          </Value>
        </div>
      )}
      {!!takeProfit && (
        <div>
          <Label>
            <I18n data={messages.take_profit} />
          </Label>
          <Value>
            <CounterMoney amount={takeProfit} pair={symbol} />
          </Value>
        </div>
      )}
      {liquidationPrice && (
        <div>
          <Label>
            <I18n data={messages.estimated_liquidation_price} />
          </Label>
          <Value>
            <CounterMoney amount={liquidationPrice} pair={symbol} />
          </Value>
        </div>
      )}
      <div>
        <Label>
          <I18n data={messages.type} />
        </Label>
        <Value data-e2e='confirmationOrderType'>{type}</Value>
      </div>
      {triggerPrice && (
        <div>
          <Label>
            <I18n data={messages.label_trigger_price} />
          </Label>
          <Value data-e2e='confirmationTriggerPrice'>
            <CounterMoney amount={triggerPrice} pair={symbol} />
          </Value>
        </div>
      )}
      {limitPrice && (
        <div>
          <Label>
            <I18n data={messages.limit_price} />
          </Label>
          <Value data-e2e='confirmationLimitPrice'>
            <CounterMoney amount={limitPrice} pair={symbol} />
          </Value>
        </div>
      )}
      {tif && (
        <div>
          <Label>
            <I18n data={messages.time_in_force} />
          </Label>
          <Value data-e2e='confirmationTimeInForce'>
            <I18n data={tif} />
          </Value>
        </div>
      )}
      {goodToDate && (
        <div>
          <Label>
            <I18n data={messages.good_to} />
          </Label>
          <Value>{serialiseDate(goodToDate)}</Value>
        </div>
      )}
      {minAmount && (
        <div>
          <Label>
            <I18n data={messages.label_minimum_amount} />
          </Label>
          <Value data-e2e='confirmationMinimumQuantity'>
            <BaseMoney amount={minAmount} pair={symbol} />
          </Value>
        </div>
      )}
      {liquidity && (
        <div>
          <Label>
            <I18n data={messages.label_liquidity} />
          </Label>
          <Value>
            <I18n data={messages.yes} />
          </Value>
        </div>
      )}
      {fees && (
        <div>
          <Label>
            <I18n data={messages.header_fee} />
          </Label>
          <Value>{fees}</Value>
        </div>
      )}
      {time && (
        <div>
          <Label>
            <I18n data={messages.order_time} />
          </Label>
          <Value>
            <FormattedDate
              day='numeric'
              hour='numeric'
              minute='numeric'
              month='numeric'
              second='numeric'
              year='numeric'
              value={time}
            />
          </Value>
        </div>
      )}
      {estimatedTotal && (
        <div>
          <Label>
            <I18n data={messages.total_estimate} />
          </Label>
          <Value data-e2e='confirmationEstimatedTotal'>{estimatedTotal}</Value>
        </div>
      )}
    </DetailsWrapper>
  )
}

export default OrderDetails
